import { useEffect, useState } from 'react';
import { Flex, Button } from '@mantine/core';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import _ from 'lodash';
import { toast } from 'react-toastify';
import instance from '../../../axios-instance';
import { updateWorkflow } from '../../../api/workflow.api';
import { setErrors } from '@jadwalh/utils';
import WorkflowInfo from './workflowInfo';
import WorkflowSkeleton from './workflowSkeleton';
import { UpdateWorkflowWrapper } from './styles';
import { ReactComponent as BackBtn } from '../../../assets/images/svg/back-btn.svg';
import Events from './events';
import Prosedure from './procedure/prosedure';
import { theme, ErrorModal } from '@jadwalh/ui';

interface FormInitialValues {
  name: string;
  event_ids: string[];
  trigger_value?: string;
  trigger_execute_time?: string;
  trigger_time_unit?: string;
  show_setting_group_event?: string;
  duration_event?: string;
  action_workflows?: any[];
}

function AddWorkflow() {
  const { formatMessage } = useIntl();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { id } = useParams();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [id, 'workflow'],
    queryFn: () => instance.get(`app/workflows/${id}`),
    select: (response): any => response.data.data,
    enabled: !!id,
    cacheTime: 0,
  });

  const defaultValues: FormInitialValues = {
    name: data?.name || '',
    event_ids: data?.events?.map((event: any) => event.id?.toString()) || [],
    trigger_value: data?.trigger_value?.toString() || '',
    trigger_execute_time: data?.trigger_execute_time?.toString() || '',
    trigger_time_unit: data?.trigger_time_unit?.toString() || '',
    show_setting_group_event: data?.show_setting_group_event?.toString() || '',
    duration_event: data?.duration_event?.toString() || '0',
    action_workflows: data?.action_workflows || [],
  };

  const methods = useForm<FormInitialValues>({ defaultValues });
  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = methods;

  const { mutateAsync, isLoading: isUpdating } = useMutation({
    mutationKey: ['updateWorkflow', id],
    mutationFn: (data: FormInitialValues) => {
      return updateWorkflow(data, Number(id));
    },
    onSuccess: () => {
      toast.success(formatMessage({ id: 'newAppointmentAdded' }));
      navigate(`/workflows`);
    },

    onError: (error: any) => {
      const errors = error.response.data.errors;
      setErrors(errors, setError);
    },
  });

  const onSubmit = (values: any) => {
    const obj = {
      name: values.name,
      event_ids: values.event_ids?.map((id: string) => parseInt(id)) || [],
    };
    refetch().then((response) => {
      if (response.data.action_workflows.length === 0) {
        setErrorMessage(formatMessage({ id: 'actionWorkflowError' }));
        setShowErrorModal(true);
        return;
      }
      if (
        response.data.trigger_value === null ||
        response.data.trigger_value === '' ||
        response.data.trigger_value === undefined
      ) {
        setErrorMessage(formatMessage({ id: 'triggerError' }));
        setShowErrorModal(true);
        return;
      }
      mutateAsync(obj);
    });
  };

  if (isLoading) {
    return <WorkflowSkeleton />;
  }

  return (
    <UpdateWorkflowWrapper>
      <Flex direction={'column'} w={{ base: '100%', md: 'auto' }}>
        <Flex
          gap="18px"
          align={'center'}
          justify={'flex-start'}
          mt={{ base: '16px', md: '16px' }}
          mb={{ base: '16px', md: '24px' }}
          px={{ base: '12px', md: '0px' }}
        >
          <Flex
            justify={'center'}
            align={'center'}
            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            onClick={() => navigate(-1)}
          >
            <BackBtn style={{ fill: '#25314C' }} />
          </Flex>
          <h2
            className="page-title"
            style={{
              fontWeight: theme.fonts.Bold,
              fontSize: '20px',
              lineHeight: '34px',
            }}
          >
            {formatMessage({ id: 'addWorkflowTitle' })}
          </h2>
        </Flex>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <Flex
              gap="16px"
              align={{ base: 'center', md: 'flex-start' }}
              direction={{ base: 'column-reverse', lg: 'row' }}
            >
              <Flex direction={'column'} className="rightSection" gap="16px">
                <Events data={data} refetch={refetch} />
                <Prosedure
                  procedures={data?.action_workflows}
                  emailData={data?.email_provider}
                  refetch={refetch}
                />
                <Flex gap="12px" pb={{ base: '0px', md: '30px' }}>
                  <Button
                    type="submit"
                    className="save-form-btn"
                    style={{
                      backgroundColor: '#000',
                      borderRadius: '8px',
                      color: 'white',
                      width: '97px',
                      height: '32px',
                      fontWeight: '400',
                      fontSize: '14px',
                      lineHeight: '20px',
                      padding: 0,
                    }}
                    loading={isUpdating}
                  >
                    {!isUpdating && formatMessage({ id: 'saveForm' })}
                  </Button>
                  <Button
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      color: '#303030',
                      minWidth: '80px',
                      border: '1px solid rgb(200,200,200)',
                      height: '32px',
                      fontWeight: '400',
                      fontSize: '14px',
                      lineHeight: '20px',
                    }}
                    onClick={() => navigate('/workflows')}
                    className="cancel-form-btn"
                  >
                    {formatMessage({ id: 'cancel' })}
                  </Button>
                </Flex>
              </Flex>
              <div className="leftSection">
                <WorkflowInfo data={data} errors={errors} />
              </div>
            </Flex>
          </form>
        </FormProvider>
        {showErrorModal && (
          <ErrorModal
            showErrorModal={showErrorModal}
            setShowErrorModal={setShowErrorModal}
            message={errorMessage}
            showCloseBtn={true}
          />
        )}
      </Flex>
    </UpdateWorkflowWrapper>
  );
}

export default AddWorkflow;
